import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { currentUserSelector } from "Auth/state";
import {
  companyInvoiceSelector,
  companySelectorCreator,
  companySubscriptionsSelector,
  customSubscriptionRequestsSelector,
  dataMetaSelector,
  getUserCompanies,
  userCompaniesSelector,
} from "Bands/state";
import {
  BAND_ACTIONS,
  IBandCustomSubscriptionRequest,
  IBandSubscription,
  IBandSubscriptionRequest,
  ICompany,
  IInvoice,
} from "Bands/types";
import { useCompanyIdFromPath, useCompanyRoute } from "Bands/utils";
import { IAPIState } from "shared/types";

import { BAND_PATHS } from "Bands/router";
import { API_ENDPOINTS } from "shared/constants";

interface CompanyContextState {
  companyId: string | null;
  company: ICompany | null;
  companyPath: string | null;
  companySubscriptions: IBandSubscription[];
  companySubscriptionRequests: IBandCustomSubscriptionRequest[];
  companyInvoices: IInvoice[];
  userCompanies: ICompany[];
  redirectToStripe: (props: {
    subscriptionRequest?: IBandSubscriptionRequest;
    companyId: string;
  }) => void;
}

const initialState: CompanyContextState = {
  companyId: null,
  company: null,
  companyPath: null,
  companySubscriptions: [],
  companySubscriptionRequests: [],
  companyInvoices: [],
  userCompanies: [],
  redirectToStripe: () => {},
};

const CompanyContext = createContext<CompanyContextState>(initialState);

export const CompanyProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { getPath } = useCompanyRoute();
  const companyId = useCompanyIdFromPath();
  const companyIdSelector = useMemo(
    () => companySelectorCreator(companyId || ""),
    [companyId]
  );
  const company = useSelector(companyIdSelector);
  const companyPath = useMemo(
    () => (companyId ? API_ENDPOINTS.COMPANY(companyId) : null),
    [companyId]
  );

  const currentUser = useSelector(currentUserSelector);
  const userCompanies = useSelector(userCompaniesSelector);

  const companyInvoices = useSelector(companyInvoiceSelector);
  const companySubscriptions = useSelector(companySubscriptionsSelector);
  const companySubscriptionRequests = useSelector(
    customSubscriptionRequestsSelector
  );

  const { api: createCompanyApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createCompany)
  );

  const redirectToStripe = useCallback(
    ({
      subscriptionRequest,
      companyId,
    }: {
      subscriptionRequest?: IBandSubscriptionRequest;
      companyId: string;
    }) => {
      const { paymentUrl } = subscriptionRequest || {};

      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        const companyDashboard = getPath(BAND_PATHS.COMPANY_DASHBOARD, {
          companyId,
        });

        history.push(companyDashboard);
      }
    },
    [getPath, history]
  );

  // fetch user companies when user id changes
  useEffect(() => {
    if (!currentUser?.id) return;

    dispatch(getUserCompanies());
  }, [dispatch, currentUser?.id]);

  // If new company created, refetch user bands
  useEffect(() => {
    if (createCompanyApi?.status === IAPIState.succeeded) {
      dispatch(getUserCompanies());
    }
  }, [createCompanyApi, dispatch]);

  return (
    <CompanyContext.Provider
      value={{
        company,
        companySubscriptions,
        companySubscriptionRequests,
        companyInvoices,
        companyId,
        companyPath,
        userCompanies,
        redirectToStripe,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompanyContext = () => useContext(CompanyContext);
